import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { CodexRail } from "@/components/site/CodexRail";
import { useEffect, useState } from "react";
import { getBookBySlug } from "@/lib/books.functions";
import { PlaceholderArt } from "@/components/site/PlaceholderArt";
import { RelatedRail } from "@/components/site/RelatedRail";
import { TopicChips } from "@/components/site/TopicChips";
import { ReadingChrome } from "@/components/site/ReadingChrome";
import { trackViewed } from "@/hooks/use-recently-viewed";

import { FavoriteButton } from "@/components/site/FavoriteButton";
import { StripeEmbeddedShopCheckout } from "@/components/site/StripeEmbeddedShopCheckout";
import { supabase } from "@/integrations/supabase/client";
import { getShopProduct } from "@/lib/shop.functions";
import { absoluteUrl, SITE_URL } from "@/lib/site";
import { BOOKS_LIBRARY_ID, breadcrumbs, ldScript, orgRef, personRef } from "@/lib/schema";
import { wisdomForBook } from "@/lib/cross-links";
import { getLessonsByTopics, type LessonTopicMatch } from "@/lib/topic-links.functions";
import { topicLabel } from "@/lib/topics";

import { track } from "@/lib/analytics";
import { ResponsiveImage } from "@/components/site/ResponsiveImage";
import { ArrowLeft, BookOpen, Lock, ExternalLink, Share2, Copy, Check } from "lucide-react";

export const Route = createFileRoute("/books/$slug")({
  loader: async ({ params }) => {
    const book = await getBookBySlug({ data: { slug: params.slug } });
    if (!book) throw notFound();
    // First-party essays that explicitly reference this book.
    const writings = wisdomForBook(book.slug).map((w) => ({
      slug: w.slug,
      title: w.title,
      category: w.category,
    }));
    // Academy lessons that share an author-written topic tag with this book.
    const lessons: LessonTopicMatch[] = book.tags?.length
      ? await getLessonsByTopics({ data: { tags: book.tags, limit: 4 } }).catch(() => [])
      : [];
    return { book, writings, lessons };
  },
  errorComponent: () => (
    <div className="mx-auto max-w-3xl px-4 py-24 text-center">
      <p className="text-gold">This book is temporarily unavailable.</p>
      <Link to="/books" className="mt-4 inline-block text-gold-luminous">← Return to books</Link>
    </div>
  ),
  head: ({ loaderData }) => {
    if (!loaderData) return { meta: [{ title: "Book not found" }, { name: "robots", content: "noindex" }] };
    const b = loaderData.book;
    const canonical = `${SITE_URL}/books/${b.slug}`;
    const authorLine = b.author ? `${b.author}${b.authorLegalName ? ` (${b.authorLegalName})` : ""}` : "Buddha KA";
    const title = `${b.title} · ${authorLine} · KAfrequency`;
    const description = b.longDescription.length > 300
      ? b.longDescription.slice(0, 297) + "…"
      : b.longDescription;
    const image = b.coverUrl ? absoluteUrl(b.coverUrl) : undefined;
    const lessons = loaderData.lessons ?? [];
    const writings = loaderData.writings ?? [];
    const relatedLinks = [
      ...writings.map((w) => absoluteUrl(`/wisdom/${w.slug}`)),
      ...lessons.map((l) => absoluteUrl(`/academy/${l.domainSlug}/lesson/${l.slug}`)),
    ];
    const isBuddhaKa = !b.author || /buddha\s*ka|curtis\s*shores/i.test(b.author);
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: `${b.title} · ${authorLine}` },
        { property: "og:description", content: description },
        { property: "og:type", content: "book" },
        { property: "og:url", content: canonical },
        { property: "og:site_name", content: "Buddha KA · KAfrequency" },
        ...(image ? [
          { property: "og:image", content: image },
          { name: "twitter:image", content: image },
        ] : []),
        { name: "twitter:card", content: image ? "summary_large_image" : "summary" },
        { name: "twitter:title", content: `${b.title} · ${authorLine}` },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: canonical }],
      scripts: [
        ldScript({
          "@context": "https://schema.org",
          "@type": "Book",
          "@id": `${canonical}#book`,
          name: b.title,
          // Reference the sitewide Person node when Buddha KA is the author,
          // so the book joins his entity graph instead of forking a new one.
          author: isBuddhaKa ? personRef() : { "@type": "Person", name: authorLine },
          ...(isBuddhaKa ? { creator: personRef() } : {}),
          publisher: orgRef(),
          isPartOf: { "@id": BOOKS_LIBRARY_ID },
          mainEntityOfPage: { "@type": "WebPage", "@id": canonical, url: canonical },
          description: b.longDescription,
          url: canonical,
          ...(image ? { image } : {}),
          ...(b.externalPurchaseUrl ? {
            offers: {
              "@type": "Offer",
              url: b.externalPurchaseUrl,
              availability: "https://schema.org/InStock",
            },
          } : {}),
          inLanguage: "en",
          ...(b.tags?.length ? { keywords: b.tags.join(", ") } : {}),
          ...(relatedLinks.length
            ? {
                relatedLink: relatedLinks,
                subjectOf: writings.map((w) => ({
                  "@type": "Article",
                  name: w.title,
                  url: absoluteUrl(`/wisdom/${w.slug}`),
                })),
                teaches: lessons.map((l) => l.title),
              }
            : {}),
        }),
        ldScript(breadcrumbs([["Books", "/books"], [b.title, `/books/${b.slug}`]])),
      ],
    };
  },

  component: BookDetail,
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-4 py-24 text-center">
      <p className="text-gold">Book not found.</p>
      <Link to="/books" className="mt-4 inline-block text-gold-luminous">← Return to books</Link>
    </div>
  ),
});

function BookDetail() {
  const { book: b, writings, lessons } = Route.useLoaderData() as {
    book: Awaited<ReturnType<typeof getBookBySlug>> & object;
    writings: Array<{ slug: string; title: string; category: string }>;
    lessons: LessonTopicMatch[];
  };
  const [product, setProduct] = useState<{ amount_cents: number; currency: string; requires_shipping: boolean } | null>(null);
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [copied, setCopied] = useState(false);

  const external = !!b.externalPurchaseUrl;

  useEffect(() => {
    trackViewed({ kind: "book", slug: b.slug, title: b.title, href: `/books/${b.slug}` });
    supabase.auth.getUser().then(({ data }) => setAuthed(!!data.user));
    // Only look up internal shop record when there is no external URL.
    if (external) return;
    getShopProduct({ data: { slug: b.slug } }).then((row) => {
      if (row && row.amount_cents && row.amount_cents >= 50) {
        setProduct({ amount_cents: row.amount_cents, currency: row.currency, requires_shipping: !!row.requires_shipping });
      }
    }).catch(() => {});
  }, [b.slug, b.title, external]);



  const price = product ? new Intl.NumberFormat("en-US", { style: "currency", currency: product.currency.toUpperCase() }).format(product.amount_cents / 100) : null;

  const authorLine = b.author ? `${b.author}${b.authorLegalName ? ` (${b.authorLegalName})` : ""}` : "Buddha KA";
  const shareUrl = absoluteUrl(`/books/${b.slug}`);

  async function share() {
    track("book_share", { slug: b.slug });
    const payload = { title: `${b.title} — ${authorLine}`, text: b.subtitle, url: shareUrl };
    try {
      const nav = typeof navigator !== "undefined" ? navigator : null;
      if (nav && typeof nav.share === "function") {
        await nav.share(payload);
        return;
      }
      if (nav && nav.clipboard) {
        await nav.clipboard.writeText(shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch {
      /* user cancelled */
    }
  }

  return (
    <article className="mx-auto max-w-5xl px-4 py-16 sm:px-6 sm:py-24">
      <ReadingChrome completionKey={`book:${b.slug}`} />
      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="text-xs uppercase tracking-[0.25em] text-muted-foreground">
        <ol className="flex flex-wrap items-center gap-2">
          <li><Link to="/" className="hover:text-gold">Home</Link></li>
          <li aria-hidden>·</li>
          <li><Link to="/books" className="hover:text-gold">Books</Link></li>
          <li aria-hidden>·</li>
          <li className="text-gold" aria-current="page">{b.title}</li>
        </ol>
      </nav>

      <Link to="/books" className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-gold">
        <ArrowLeft size={14} /> Back to Books
      </Link>

      <div className="mt-8 grid gap-10 md:grid-cols-[1fr_1.2fr]">
        {b.coverUrl ? (
          <div className="max-w-sm">
            <div className="overflow-hidden rounded-lg border hairline bg-temple shadow-gold">
              <ResponsiveImage
                src={b.coverUrl}
                alt={b.coverAlt ?? `${b.title} — cover art`}
                priority
                sizes="(max-width: 768px) 92vw, 384px"
                className="h-auto w-full object-cover"
              />
            </div>
          </div>
        ) : (
          <PlaceholderArt label={`[${b.title} cover]`} aspect="portrait" className="max-w-sm" />
        )}

        <div>
          {b.category ? (
            <p className="text-xs uppercase tracking-[0.3em] text-gold">{b.category}</p>
          ) : (
            <p className="text-xs uppercase tracking-[0.3em] text-gold">Book</p>
          )}
          <h1 className="mt-3 font-serif text-4xl sm:text-5xl">{b.title}</h1>
          <p className="mt-3 font-serif text-lg italic text-ivory/85">{b.subtitle}</p>
          <p className="mt-3 text-sm text-muted-foreground">
            by <span className="text-ivory">{authorLine}</span>
          </p>
          <p className="mt-6 whitespace-pre-line text-muted-foreground">{b.longDescription}</p>

          {/* Purchase / read CTA */}
          {external ? (
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <a
                href={b.externalPurchaseUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track("book_external_click", { slug: b.slug, destination: "apple_books" })}
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-gold-gradient px-6 py-3 text-sm font-semibold text-obsidian shadow-gold transition hover:shadow-halo"
              >
                {b.externalPurchaseLabel ?? "Read on Apple Books"}
                <ExternalLink size={14} className="transition-transform group-hover:translate-x-0.5" />
              </a>
              <a
                href={b.externalPurchaseUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track("book_purchase_click", { slug: b.slug, destination: "apple_books" })}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-gold px-6 py-3 text-sm text-gold-luminous hover:bg-gold hover:text-obsidian"
              >
                Purchase on Apple Books
              </a>
            </div>
          ) : product ? (
            <>
              <p className="mt-6 font-serif text-3xl text-gold-luminous">{price}</p>
              {!showCheckout ? (
                <button
                  type="button"
                  disabled={authed === null}
                  onClick={() => setShowCheckout(true)}
                  className="mt-6 rounded-full bg-gold-gradient px-6 py-3 text-sm font-semibold text-obsidian shadow-gold disabled:opacity-60"
                >
                  Buy — {price}
                </button>
              ) : (
                <div className="mt-6">
                  <StripeEmbeddedShopCheckout
                    slug={b.slug}
                    requiresShipping={product.requires_shipping}
                    isAuthenticated={!!authed}
                  />
                </div>
              )}
            </>
          ) : (
            <>
              <div className="mt-6 flex flex-wrap gap-2" aria-label="Available formats">
                {["Paperback", "Hardcover", "eBook", "Audiobook"].map((f) => (
                  <span key={f} className="inline-flex items-center gap-1.5 rounded-full border hairline px-4 py-2 text-xs text-muted-foreground">
                    <Lock size={11} aria-hidden /> {f} · pending
                  </span>
                ))}
              </div>
              <p className="mt-4 text-xs text-muted-foreground">Purchase activates once this title is priced and set live in the admin shop.</p>
            </>
          )}

          {b.digitalTitleSlug && (
            <div className="mt-6">
              <Link
                to="/library/read/$slug"
                params={{ slug: b.digitalTitleSlug }}
                onClick={() => track("book_read_full_edition", { slug: b.slug, digital_title_slug: b.digitalTitleSlug })}
                className="inline-flex items-center gap-2 rounded-full border border-gold px-6 py-3 text-sm text-gold-luminous hover:bg-gold hover:text-obsidian"
              >
                <BookOpen size={14} /> Read the full edition in the Library
              </Link>
            </div>
          )}


          {/* Share + favorite */}
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <FavoriteButton kind="book" slug={b.slug} />
            <button
              type="button"
              onClick={share}
              aria-label="Share this book"
              className="inline-flex items-center gap-2 rounded-full border hairline px-4 py-2 text-xs text-muted-foreground hover:border-gold hover:text-gold"
            >
              {copied ? (
                <><Check size={12} /> Link copied</>
              ) : (
                <><Share2 size={12} /> Share</>
              )}
            </button>
            <button
              type="button"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(shareUrl);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                  track("book_copy_link", { slug: b.slug });
                } catch { /* ignore */ }
              }}
              aria-label="Copy link"
              className="inline-flex items-center gap-2 rounded-full border hairline px-4 py-2 text-xs text-muted-foreground hover:border-gold hover:text-gold"
            >
              <Copy size={12} /> Copy link
            </button>
          </div>
        </div>
      </div>

      {writings.length > 0 && (
        <nav aria-label="Writings about this book" className="mt-12 rounded-2xl border hairline bg-charcoal p-6">
          <p className="text-xs uppercase tracking-[0.3em] text-gold">Written about in Wisdom</p>
          <ul className="mt-4 space-y-3 text-sm text-muted-foreground">
            {writings.map((w) => (
              <li key={w.slug}>
                <Link to="/wisdom/$slug" params={{ slug: w.slug }} className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">
                  {w.title}
                </Link>
                <span> — {w.category}</span>
              </li>
            ))}
          </ul>
        </nav>
      )}

      {lessons.length > 0 && (
        <nav aria-label="Related Academy lessons" className="mt-6 rounded-2xl border hairline bg-charcoal p-6">
          <p className="text-xs uppercase tracking-[0.3em] text-gold">Study this in the Academy</p>
          <ul className="mt-4 space-y-3 text-sm text-muted-foreground">
            {lessons.map((l) => (
              <li key={`${l.domainSlug}/${l.slug}`}>
                <Link
                  to="/academy/$domain/lesson/$lesson"
                  params={{ domain: l.domainSlug, lesson: l.slug }}
                  className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold"
                >
                  {l.title}
                </Link>
                <span> — {l.domainName}{l.topics.length ? ` · shares the topic of ${topicLabel(l.topics)}` : ""}</span>
              </li>
            ))}
          </ul>
        </nav>
      )}

      <div className="mx-auto max-w-5xl px-4 sm:px-6">
        <TopicChips tags={b.tags} text={[b.title, b.subtitle, b.longDescription].filter(Boolean).join(" ")} className="mb-10" />
      </div>
      <div className="mx-auto max-w-5xl px-4 sm:px-6">
        <CodexRail entityKind="book" entityRef={b.slug} heading="Concepts this book teaches" />
      </div>
      <RelatedRail kind="book" slug={b.slug} heading="Companion transmissions" />

    </article>
  );
}
